import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useScrollTop } from '@hivebrite/react-hooks';

import { useSessionInfoContext } from 'hb-react/frontoffice/contexts/SessionInfoContext';
import {
  PAGES,
  STEPS,
} from 'hb-react/shared/apps/Memberships/containers/PurchaseMembership/constants';
import { money } from 'hb-react/shared/apps/Memberships/utils/money';
import { CART_ITEM_TYPES } from 'hb-react/shared/components/ShoppingCard/constants/cartConstants';
import { useShoppingCard } from 'hb-react/shared/components/ShoppingCard/hooks/useShoppingCard';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { goToPaymentPage } from 'hb-react/shared/utils/paymentPage';

import {
  addShoppingCardItemsActions,
  getBreadcrumbs,
  getPromoCodeIds,
} from './utils';

export const usePurchaseMembership = () => {
  const { flagEvPaymentPageMemberships } = useFeatureFlag();
  const { page } = useParams();
  const currentPage = page.toUpperCase();

  const {
    data: {
      cart = {},
      associated_items: {
        memberships = [],
        donations = [],
        addons = [],
        donation_overlay_headline: donationOverlayHeadline,
      } = {},
      _metadata = {},
    } = {},
    isLoading,
    actions,
  } = useShoppingCard();

  const {
    cart_items: cartItems = [],
    total_in_cents: amount,
    status,
    user_can_trial_any_cart_item: userCanTrialAnyCartItem,
    cart_has_100_percent_discounst_and_requires_billing_info:
      requiresBillingInfo,
    currency,
  } = cart;

  const { memberships: membershipsSessionInfo } = useSessionInfoContext();

  const navigate = useNavigate();

  const { skip_billing_form_for_free_items: featureSkipBillingForm = false } =
    membershipsSessionInfo?.programme || {};

  const [currentStep, setCurrentStep] = useState(currentPage);

  const [donationDisplayed, setDonationDisplayed] = useState(false);

  const withPayment =
    amount > 0 || userCanTrialAnyCartItem || requiresBillingInfo;

  const hasDonations = cartItems.find(
    (item) => item.line_itemable_type === CART_ITEM_TYPES.DONATION,
  );

  useEffect(() => {
    if (
      status === 'paid' ||
      [STEPS.SUCCESS, STEPS.COMPLETED].includes(currentStep)
    ) {
      navigate('completed');
    }
  }, [currentStep, status]);

  useScrollTop([currentPage]);

  useEffect(() => {
    if (hasDonations) {
      setDonationDisplayed(true);
    }
  }, [hasDonations]);

  useOnStep({
    isLoading,
    currentStep,
    currentPage,
  });

  useEffect(() => {
    if (currentStep !== STEPS.REGISTRATION_COMPLETED) {
      return;
    }

    if (!featureSkipBillingForm || withPayment) {
      return setCurrentStep(STEPS.BILLING);
    }

    actions
      .complete()
      .then(() => {
        setCurrentStep(STEPS.COMPLETED);
      })
      .catch(() => {
        setCurrentStep(STEPS.REGISTRATION);
      });
  }, [featureSkipBillingForm, withPayment, currentStep]);

  useEffect(() => {
    if (currentStep === STEPS.REGISTRATION) {
      const promoCodeIds = getPromoCodeIds(cartItems);

      if (promoCodeIds.length > 0) {
        promoCodeIds.map((id) => actions.items.remove(id));
      }
    }
  }, [currentStep]);

  const onBillingComplete = async (values) => {
    try {
      await actions.update(values);

      if (!withPayment) {
        await actions.complete();
        setCurrentStep(STEPS.COMPLETED);

        return;
      }

      if (flagEvPaymentPageMemberships) {
        goToPaymentPage({ cartId: cart.id });
      } else {
        navigate('../payment');
      }
    } catch {
      setCurrentStep(STEPS.BILLING);
    }
  };

  const onRegistrationComplete = () => {
    if (donations.length > 0 && !donationDisplayed) {
      setDonationDisplayed(true);

      return setCurrentStep(STEPS.DONATIONS);
    }

    setCurrentStep(STEPS.REGISTRATION_COMPLETED);
  };

  const onDonationSubmit = async ({ donations = [] }) => {
    const anyDonations = donations.filter(
      ({ amount = 0, cartItem }) => amount > 0 || cartItem,
    );

    if (anyDonations.length > 0) {
      const newDonations = anyDonations.filter(({ cartItem }) => !cartItem);
      const existingDonations = anyDonations.filter(({ cartItem }) => cartItem);

      if (newDonations.length > 0) {
        await Promise.all(
          newDonations.map(({ amount: price, id, type }) =>
            actions.items.add({
              line_itemable_id: id,
              type,
              total_in_cents: money(price, currency.code).cents,
            }),
          ),
        );
      }

      await Promise.all(
        existingDonations.map(({ cartItem, id, amount, type }) => {
          if (amount > 0) {
            return actions.items.update({
              id: cartItem.id,
              total_in_cents: money(amount, currency.code).cents,
              line_itemable_id: id,
              type,
            });
          }

          return actions.items.remove(cartItem.id);
        }),
      );
    }

    setCurrentStep(STEPS.REGISTRATION_COMPLETED);
  };

  const onDonationClose = () => {
    setCurrentStep(STEPS.REGISTRATION_COMPLETED);
  };

  const onMembershipUpdate = ({ membership, paymentOption }) => {
    const { cartItem = {} } =
      membership?.payment_options?.find(({ cartItem }) => cartItem?.id) ||
      membership;

    const { id, type } = paymentOption?.id ? paymentOption : membership;

    if (cartItem?.id) {
      actions.items.update({
        id: cartItem.id,
        type,
        line_itemable_id: id,
        recurring: paymentOption.recurring_payment_enabled,
        quantity: 1,
      });
    }
  };

  const onAddonSelect = ({ id, cartItem, type }) => {
    if (cartItem) {
      actions.items.remove(cartItem.id);
    } else {
      actions.items.add({
        type,
        line_itemable_id: id,
      });
    }
  };

  const goToMembershipIndexPage = () => {
    if (memberships?.[0]?._links?.index) {
      return navigate(memberships[0]._links.index);
    }

    return navigate(Routes.memberships_path());
  };

  return {
    navigation: getBreadcrumbs({
      currentStep,
      setCurrentStep,
      withPaymentStep: !featureSkipBillingForm || withPayment,
    }),
    currentStep,
    currentPage,
    memberships,
    donations,
    eligibleForPromocodes: _metadata?.eligible_for_promocodes ?? false,
    addons: addons.map((addon) => ({
      ...addon,
      selected: !!addon.cartItem,
      onSelect: () => onAddonSelect(addon),
    })),
    cartItems: cartItems.map((item) =>
      addShoppingCardItemsActions({
        item,
        currentPage,
        setCurrentStep,
        actions,
      }),
    ),
    registrationSubmitButtonText:
      featureSkipBillingForm && !withPayment
        ? __('Submit')
        : s__('Memberships|Proceed to Billing information'),
    isLoading,
    withPayment,
    goToRegistrationStep: () => setCurrentStep(STEPS.REGISTRATION),
    goToMembershipIndexPage,
    onMembershipUpdate,
    onRegistrationComplete,
    onBillingComplete,
    onDonationSubmit,
    onDonationClose,
    donationOverlayHeadline,
  };
};

const useOnStep = ({ currentStep, isLoading, currentPage }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const targetPage = PAGES[currentStep];

    if (targetPage && currentPage !== targetPage) {
      navigate(`../${targetPage.toLowerCase()}`);
    }
  }, [currentStep, isLoading, navigate, currentPage]);
};
